import React, { useState, useCallback, useRef } from 'react';
import { Phone, PhoneOff, Mic, MicOff, Volume2, Shield, MapPin, Navigation } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';

// Vapi Public Key
const VAPI_PUBLIC_KEY = process.env.REACT_APP_VAPI_PUBLIC_KEY || 'your-public-key';

// Simple intl helper for translations
const intl = {
  formatMessage: ({ id, defaultMessage }) => {
    const messages = {
      'voice.callActive': 'Appel en cours',
      'voice.endCall': 'Raccrocher',
      'voice.startCall': 'Appeler',
      'voice.title': 'Assistant Vocal',
      'voice.speaking': 'Parlez maintenant...',
      'voice.connecting': 'Connexion...',
      'voice.ready': 'Prêt à vous aider',
    };
    return messages[id] || defaultMessage || id;
  }
};

const VoiceAssistantButton = ({ 
  assistantId,
  onCallStart,
  onCallEnd,
  onTranscript,
  onError,
  className = '',
  variant = 'default', // 'default', 'floating', 'minimal'
  context = {}, // Additional context to pass to the assistant
  preferredLanguage = 'fr' // Language code: 'fr', 'en', 'de', etc.
}) => {
  // Use provided language or default to French
  const language = preferredLanguage || 'fr';
  const [vapi, setVapi] = useState(null);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [error, setError] = useState(null);
  
  // Connection status feedback
  const [connectionStatus, setConnectionStatus] = useState(null); // null, 'connecting', 'mic_ready', 'speaking'
  
  // Consent dialog state
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [hasSeenConsent, setHasSeenConsent] = useState(() => {
    return localStorage.getItem('voice_consent_given') === 'true';
  });
  
  // Search results with GPS navigation
  const [searchResults, setSearchResults] = useState(null);
  const [showNavigationPanel, setShowNavigationPanel] = useState(false);
  
  // Microphone device ID for Daily
  const selectedMicIdRef = useRef(null);

  // Vapi ref
  const vapiRef = useRef(null);
  
  // Reference to keep the permission stream alive
  const permissionStreamRef = useRef(null);

  // Start call
  const startCall = useCallback(async () => {
    if (!assistantId) {
      console.error('Assistant ID missing');
      setError('Assistant non configuré');
      return;
    }

    try {
      setIsConnecting(true);
      setError(null);
      setTranscript([]);
      setSearchResults(null);
      setShowNavigationPanel(false);
      setConnectionStatus('connecting');
      
      // ============================================================
      // FIX "response body already used" - Dynamic import each call
      // This forces a fresh module load, avoiding cached response bodies
      // ============================================================
      console.log('🔄 Dynamic import of VAPI SDK...');
      
      // Clear any existing instance
      if (vapiRef.current) {
        try {
          vapiRef.current.removeAllListeners?.();
          vapiRef.current.stop?.();
        } catch (e) {
          console.warn('Cleanup warning:', e);
        }
        vapiRef.current = null;
      }
      
      // Dynamic import - creates fresh module context
      const VapiModule = await import('@vapi-ai/web');
      const Vapi = VapiModule.default;
      
      console.log('🆕 Creating fresh VAPI instance...');
      const freshVapi = new Vapi(VAPI_PUBLIC_KEY);
      
      if (!freshVapi) {
        setError('Impossible de créer l\'instance VAPI');
        setIsConnecting(false);
        return;
      }
      
      // Store ref
      vapiRef.current = freshVapi;
      setVapi(freshVapi);
      
      // Attach all listeners to the fresh instance
      
      freshVapi.on('call-start', async () => {
        console.log('📞 Call started');
        setIsCallActive(true);
        setIsConnecting(false);
        setError(null);
        setConnectionStatus('mic_ready');
        
        // ============================================================
        // SOLUTION RADICALE VAPI SUPPORT: Forcer le micro via Daily
        // ============================================================
        console.log('═══════════════════════════════════════════════════════════');
        console.log('🎤 [CALL-START] FORCING DAILY MICROPHONE');
        console.log('═══════════════════════════════════════════════════════════');
        
        try {
          const daily = freshVapi.call;
          
          console.log('📌 [LOG 1] selectedMicIdRef.current =', selectedMicIdRef.current);
          console.log('🔍 Daily object exists:', daily ? 'YES ✅' : 'NO ❌');
          
          if (daily && selectedMicIdRef.current) {
            await daily.setInputDevicesAsync({ audioDeviceId: selectedMicIdRef.current });
            console.log('✅ setInputDevicesAsync() completed');
            
            const devices = await daily.enumerateDevices();
            console.log('📌 [LOG 2] DAILY DEVICES =', JSON.stringify(devices, null, 2));
            
            if (daily.getInputDevicesAsync) {
              const selected = await daily.getInputDevicesAsync();
              console.log('📌 [LOG 3] getInputDevicesAsync() =', JSON.stringify(selected, null, 2));
            }
            
            console.log('✅ MICROPHONE FORCING COMPLETE');
          }
        } catch (dailyError) {
          console.error('❌ Error forcing Daily microphone:', dailyError);
        }
        
        onCallStart?.();
      });

      freshVapi.on('call-end', () => {
        console.log('📴 Call ended');
        setIsCallActive(false);
        setIsConnecting(false);
        setIsMuted(false);
        setConnectionStatus(null);
        
        if (permissionStreamRef.current) {
          permissionStreamRef.current.getTracks().forEach(t => t.stop());
          permissionStreamRef.current = null;
        }
        
        onCallEnd?.();
      });

      freshVapi.on('speech-start', () => {
        console.log('🗣️ Assistant started speaking');
        setConnectionStatus('speaking');
      });

      freshVapi.on('message', (message) => {
        const timestamp = new Date().toLocaleTimeString();
        
        if (message.type === 'transcript') {
          const status = message.transcriptType || (message.transcript ? 'final' : 'partial');
          console.log(`🎤 [${timestamp}] TRANSCRIPT (${message.role}) [${status}]:`, message.transcript || message.text || '(empty)');
          
          if (message.transcript) {
            const newTranscript = { role: message.role, text: message.transcript, timestamp: new Date().toISOString() };
            setTranscript(prev => [...prev, newTranscript]);
            onTranscript?.(newTranscript);
          }
        } else if (message.type === 'speech-update') {
          console.log(`🔊 [${timestamp}] SPEECH-UPDATE: ${message.role} ${message.status}`, message);
        }
        
        if (message.type === 'function-call-result' || message.type === 'function-call') {
          const result = message.result || message.functionCall?.result;
          if (result?.doctors) setSearchResults(result);
          if (result?.show_navigation === true) setShowNavigationPanel(true);
        }
      });

      freshVapi.on('volume-level', (level) => setVolumeLevel(level));

      freshVapi.on('error', (e) => {
        console.error('❌ Vapi error RAW:', e);
        
        // Stringify the entire error for debugging
        let rawError = '';
        try {
          rawError = JSON.stringify(e, Object.getOwnPropertyNames(e), 2);
        } catch {
          rawError = String(e);
        }
        console.error('❌ Vapi error STRINGIFIED:', rawError);
        
        const errorMessage = e?.error?.message || e?.message || e?.errorMsg || '';
        const errorCode = e?.error?.code || e?.code || e?.errorCode || '';
        const errorType = e?.error?.type || e?.type || '';
        
        // Show RAW error for debugging - TEMPORARY
        let errorMsg = `DEBUG: ${errorType || errorCode || 'no-type'} | ${errorMessage || rawError.substring(0, 100)}`;
        
        if (errorMessage.includes('response body is already used') || errorMessage.includes('body stream already read')) {
          errorMsg = '🔄 Erreur temporaire - Rafraîchissez la page (F5)';
          // Clean up current instance
          if (vapiRef.current) {
            try {
              vapiRef.current.removeAllListeners?.();
              vapiRef.current.stop?.();
            } catch (cleanupErr) {}
            vapiRef.current = null;
          }
        }
        
        setError(errorMsg);
        setIsConnecting(false);
        onError?.(e);
      });
      
      // ============================================================
      // PATCH RADICAL VAPI SUPPORT - Force mic BEFORE vapi.start()
      // 1. getUserMedia({ audio: true }) OBLIGATOIRE pour avoir des IDs valides
      // 2. enumerateDevices() pour récupérer le micId exact
      // 3. Log le micId choisi pour debug
      // ============================================================
      try {
        console.log('🎤 [STEP 1] Forcing getUserMedia BEFORE enumerateDevices...');
        
        // OBLIGATOIRE: getUserMedia d'abord sinon enumerateDevices renvoie des IDs vides
        const permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('✅ [STEP 1] getUserMedia OK - permissions granted');
        
        // STEP 2: Maintenant enumerateDevices aura les vrais IDs
        console.log('🎤 [STEP 2] Calling enumerateDevices...');
        const devices = await navigator.mediaDevices.enumerateDevices();
        const mics = devices.filter(d => d.kind === 'audioinput');
        
        // LOG DÉTAILLÉ pour VAPI Support
        console.log('🔍 [STEP 2] ALL AUDIO INPUT DEVICES:');
        mics.forEach((mic, i) => {
          console.log(`   [${i}] deviceId: ${mic.deviceId}`);
          console.log(`   [${i}] label: ${mic.label}`);
          console.log(`   [${i}] groupId: ${mic.groupId}`);
        });
        
        // Choisir le micro: préférer celui avec un vrai ID (pas "default" ou vide)
        let selectedMic = mics.find(m => m.deviceId && m.deviceId !== 'default' && m.deviceId.length > 10);
        if (!selectedMic) selectedMic = mics[0]; // Fallback au premier
        
        const micId = selectedMic?.deviceId;
        
        // STORE THE MIC ID FOR DAILY TO USE AFTER CALL-START
        selectedMicIdRef.current = micId;
        console.log('🎤 [STEP 2] selectedMicIdRef.current =', selectedMicIdRef.current);
        console.log('🎤 [STEP 2] Selected mic label:', selectedMic?.label);
        
        // STEP 3: Créer le stream final avec le deviceId exact
        console.log('🎤 [STEP 3] Creating final stream with exact deviceId...');
        
        // Stop the permission stream first
        permissionStream.getTracks().forEach(t => t.stop());
        
        // Create stable stream with Android-safe constraints
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            deviceId: micId ? { exact: micId } : undefined,
            echoCancellation: false,      // OFF for Android stability
            noiseSuppression: false,      // OFF for Android stability
            autoGainControl: true,        // ON - helps normalize volume
            channelCount: 1,              // Mono for voice
          } 
        });
        
        // KEEP THIS STREAM ALIVE - Don't stop it!
        permissionStreamRef.current = stream;
        
        // DIAGNOSTIC LOGGING FINAL
        const audioTracks = stream.getAudioTracks();
        console.log('🔍 [STEP 3] Audio tracks count:', audioTracks.length);
        if (audioTracks.length > 0) {
          const track = audioTracks[0];
          const settings = track.getSettings();
          console.log('🔍 [STEP 3] Track label:', track.label);
          console.log('🔍 [STEP 3] Track deviceId from settings:', settings.deviceId);
          console.log('🔍 [STEP 3] Track settings:', settings);
        }
        
        console.log('✅ [STEP 3] Microphone stream ready - keeping alive for call');
      } catch (micError) {
        console.error('❌ Microphone error:', micError);
        setConnectionStatus(null);
        if (micError.name === 'NotAllowedError' || micError.name === 'PermissionDeniedError') {
          setError('🎤 Microphone bloqué. Autorisez l\'accès au micro dans votre navigateur.');
        } else if (micError.name === 'NotFoundError') {
          setError('🎤 Aucun microphone détecté. Branchez un micro et réessayez.');
        } else {
          setError('🎤 Erreur microphone: ' + micError.message);
        }
        setIsConnecting(false);
        return;
      }
      
      console.log('Starting VAPI call with:', {
        assistantId,
        publicKey: VAPI_PUBLIC_KEY,
        language
      });
      
      // Start call with language variable - USE freshVapi not vapi state
      await freshVapi.start(assistantId, {
        variableValues: {
          preferredLanguage: language
        }
      });
      
      console.log('VAPI call started successfully');
    } catch (e) {
      console.error('Failed to start call - Full error:', e);
      console.error('Error name:', e?.name);
      console.error('Error message:', e?.message);
      console.error('Error stack:', e?.stack);
      // Show detailed error to help debugging
      const errorDetail = e?.message || e?.toString() || 'Erreur inconnue';
      setError(`Erreur: ${errorDetail}`);
      setIsConnecting(false);
      // Clean up stream on error
      if (permissionStreamRef.current) {
        permissionStreamRef.current.getTracks().forEach(t => t.stop());
        permissionStreamRef.current = null;
      }
    }
  }, [assistantId, language, onCallStart, onCallEnd, onTranscript, onError]);

  // Handle call button click - show consent if needed
  const handleCallClick = useCallback(() => {
    if (hasSeenConsent) {
      startCall();
    } else {
      setShowConsentDialog(true);
    }
  }, [hasSeenConsent, startCall]);

  // Handle consent acceptance
  const handleConsentAccept = useCallback(() => {
    if (consentGiven) {
      localStorage.setItem('voice_consent_given', 'true');
      setHasSeenConsent(true);
      setShowConsentDialog(false);
      startCall();
    }
  }, [consentGiven, startCall]);

  // Consent Dialog Component - Compact for mobile
  const ConsentDialog = () => (
    <Dialog open={showConsentDialog} onOpenChange={setShowConsentDialog}>
      <DialogContent className="max-w-sm mx-4 max-h-[85vh] overflow-y-auto">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Shield className="w-4 h-4 text-blue-600" />
            Consentement appel
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-2 space-y-3">
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              <strong>RGPD :</strong> L'appel peut être enregistré (max 30 jours). 
              Données traitées conformément à notre politique de confidentialité.
            </p>
          </div>
          
          <div className="flex items-start gap-2">
            <Checkbox
              id="voice-consent"
              checked={consentGiven}
              onCheckedChange={setConsentGiven}
              className="mt-0.5"
              data-testid="voice-consent-checkbox"
            />
            <Label htmlFor="voice-consent" className="text-xs leading-tight cursor-pointer">
              J'accepte l'enregistrement et le traitement de mes données vocales
            </Label>
          </div>
        </div>
        
        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={() => setShowConsentDialog(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleConsentAccept}
            disabled={!consentGiven}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="voice-consent-accept-btn"
          >
            <Phone className="w-3 h-3 mr-1" />
            Appeler
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // End call
  const endCall = useCallback(() => {
    if (vapi) {
      vapi.stop();
    }
  }, [vapi]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (vapi) {
      const newMutedState = !isMuted;
      vapi.setMuted(newMutedState);
      setIsMuted(newMutedState);
    }
  }, [vapi, isMuted]);

  // Render based on variant
  if (variant === 'floating') {
    return (
      <>
        <ConsentDialog />
        <div className={`fixed bottom-24 right-6 z-[9999] ${className}`}>
          {/* Error message */}
          {error && (
            <div className="absolute bottom-20 right-0 bg-red-100 text-red-700 px-4 py-2 rounded-lg shadow-lg mb-2 whitespace-nowrap">
              {error}
            </div>
          )}
          
          {/* Call controls */}
          {/* Connection Status Banner - Shows during connection */}
          {connectionStatus && connectionStatus !== 'speaking' && (
            <div className="absolute bottom-24 right-0 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl shadow-2xl p-4 mb-2 min-w-[220px] animate-pulse">
              <div className="flex items-center gap-3">
                {connectionStatus === 'connecting' && (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <div>
                      <p className="font-medium text-sm">Connexion...</p>
                      <p className="text-xs text-blue-100">Docteur-booking arrive</p>
                    </div>
                  </>
                )}
                {connectionStatus === 'mic_ready' && (
                  <>
                    <Mic className="w-5 h-5 text-green-300" />
                    <div>
                      <p className="font-medium text-sm">Micro activé</p>
                      <p className="text-xs text-blue-100">Un instant...</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          
          {isCallActive && (
            <div className="absolute bottom-20 right-0 bg-white rounded-lg shadow-xl p-4 mb-2 min-w-[200px]">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-green-600 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  {intl.formatMessage({ id: 'voice.callActive', defaultMessage: 'Appel en cours' })}
                </span>
                <Volume2 className="w-4 h-4 text-gray-400" style={{ opacity: 0.3 + volumeLevel * 0.7 }} />
              </div>
              
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={isMuted ? "destructive" : "outline"}
                  onClick={toggleMute}
                  className="flex-1"
                >
                  {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={endCall}
                  className="flex-1"
                >
                  <PhoneOff className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Main button - Larger and more visible */}
          <div className="relative">
            {/* Pulse ring animation for visibility */}
            {!isCallActive && !isConnecting && (
              <div className="absolute inset-0 w-20 h-20 rounded-full bg-blue-400 animate-ping opacity-30"></div>
            )}
            <Button
              onClick={isCallActive ? endCall : handleCallClick}
              disabled={isConnecting}
              className={`
                w-20 h-20 rounded-full shadow-2xl transition-all duration-300 relative z-10
                ${isCallActive 
                  ? 'bg-red-500 hover:bg-red-600 ring-4 ring-red-300' 
                  : 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 ring-4 ring-blue-300/50'
                }
                ${isConnecting ? 'animate-pulse' : ''}
              `}
              data-testid="voice-assistant-floating-btn"
            >
              {isConnecting ? (
                <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin" />
              ) : isCallActive ? (
                <PhoneOff className="w-8 h-8 text-white" />
              ) : (
                <Phone className="w-8 h-8 text-white" />
              )}
            </Button>
            {/* Label below button */}
            {!isCallActive && !isConnecting && (
              <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                <span className="text-xs font-medium bg-gray-900 text-white px-3 py-1 rounded-full shadow-lg">
                  Appeler 24/7
                </span>
              </div>
            )}
          </div>
          
          {/* GPS Navigation Panel - Shows when external doctors are found */}
          {showNavigationPanel && searchResults && searchResults.doctors && (
            <div className="absolute bottom-24 right-0 bg-white rounded-xl shadow-2xl p-4 mb-2 w-80 max-h-96 overflow-y-auto border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  Médecins à proximité
                </h3>
                <button 
                  onClick={() => setShowNavigationPanel(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded mb-3">
                Aucun médecin inscrit sur drbooking. Voici les cabinets trouvés via Google Maps :
              </p>
              
              <div className="space-y-3">
                {searchResults.doctors.slice(0, 3).map((doctor, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <p className="font-medium text-gray-800 text-sm">{doctor.name}</p>
                    {doctor.address && (
                      <p className="text-xs text-gray-500 mt-1">{doctor.address}</p>
                    )}
                    {doctor.rating && (
                      <p className="text-xs text-yellow-600 mt-1">⭐ {doctor.rating}/5</p>
                    )}
                    {doctor.phone && (
                      <a href={`tel:${doctor.phone}`} className="text-xs text-blue-600 hover:underline block mt-1">
                        📞 {doctor.phone}
                      </a>
                    )}
                    
                    {/* Navigation buttons */}
                    <div className="flex gap-2 mt-3">
                      {doctor.google_maps_url && (
                        <a
                          href={doctor.google_maps_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 bg-blue-500 hover:bg-blue-600 text-white text-xs py-2 px-3 rounded-lg flex items-center justify-center gap-1 transition-colors"
                          data-testid={`gps-google-${index}`}
                        >
                          <MapPin className="w-3 h-3" />
                          Google Maps
                        </a>
                      )}
                      {doctor.waze_url && (
                        <a
                          href={doctor.waze_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white text-xs py-2 px-3 rounded-lg flex items-center justify-center gap-1 transition-colors"
                          data-testid={`gps-waze-${index}`}
                        >
                          <Navigation className="w-3 h-3" />
                          Waze
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-gray-400 text-center mt-3">
                Cliquez sur un bouton pour ouvrir la navigation GPS
              </p>
            </div>
          )}
        </div>
      </>
    );
  }

  if (variant === 'minimal') {
    return (
      <>
        <ConsentDialog />
        <Button
          onClick={isCallActive ? endCall : handleCallClick}
          disabled={isConnecting}
          variant={isCallActive ? "destructive" : "default"}
          size="sm"
          className={className}
          data-testid="voice-assistant-minimal-btn"
        >
          {isConnecting ? (
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
          ) : isCallActive ? (
            <PhoneOff className="w-4 h-4 mr-2" />
          ) : (
            <Phone className="w-4 h-4 mr-2" />
          )}
          {isCallActive 
            ? intl.formatMessage({ id: 'voice.endCall', defaultMessage: 'Raccrocher' })
            : intl.formatMessage({ id: 'voice.startCall', defaultMessage: 'Appeler' })
          }
        </Button>
      </>
    );
  }

  // Large button variant for combined interface (no fixed positioning)
  if (variant === 'minimal-large') {
    return (
      <>
        <ConsentDialog />
        <div className="flex flex-col items-center">
          {/* Error message */}
          {error && (
            <div className="bg-red-100 text-red-700 px-3 py-2 rounded-lg text-xs mb-2 max-w-[200px] text-center">
              {error}
            </div>
          )}
          
          {/* Connection status */}
          {connectionStatus && connectionStatus !== 'speaking' && (
            <div className="bg-blue-500 text-white px-3 py-2 rounded-lg text-xs mb-2 animate-pulse">
              {connectionStatus === 'connecting' ? 'Connexion...' : 'Micro activé'}
            </div>
          )}
          
          {/* Call controls when active */}
          {isCallActive && (
            <div className="flex gap-2 mb-2">
              <Button
                size="sm"
                variant={isMuted ? "destructive" : "outline"}
                onClick={toggleMute}
              >
                {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </Button>
              <Button size="sm" variant="destructive" onClick={endCall}>
                <PhoneOff className="w-4 h-4" />
              </Button>
            </div>
          )}
          
          {/* Main button */}
          {!isCallActive && (
            <Button
              onClick={handleCallClick}
              disabled={isConnecting}
              className={`
                w-16 h-16 rounded-full shadow-xl transition-all
                bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700
                ${isConnecting ? 'animate-pulse' : ''}
              `}
              data-testid="voice-assistant-large-btn"
            >
              {isConnecting ? (
                <div className="w-7 h-7 border-3 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Phone className="w-7 h-7 text-white" />
              )}
            </Button>
          )}
          
          {/* Label */}
          <span className="text-xs text-gray-500 mt-1">
            {isCallActive ? 'Appel en cours' : isConnecting ? 'Connexion...' : 'Appel direct'}
          </span>
        </div>
      </>
    );
  }

  // Default variant - full card
  return (
    <>
      <ConsentDialog />
      <div className={`bg-white rounded-xl shadow-lg p-6 ${className}`} data-testid="voice-assistant-card">
        <div className="text-center mb-4">
          <div className={`
            w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 transition-all duration-300
            ${isCallActive 
              ? 'bg-green-100 ring-4 ring-green-200' 
              : 'bg-blue-100'
            }
          `}>
            {isConnecting ? (
              <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
            ) : isCallActive ? (
              <Phone className="w-10 h-10 text-green-600 animate-pulse" />
            ) : (
              <Phone className="w-10 h-10 text-blue-600" />
            )}
          </div>
          
          <h3 className="text-lg font-semibold text-gray-800 mb-1">
            {intl.formatMessage({ id: 'voice.title', defaultMessage: 'Assistant Vocal' })}
          </h3>
          <p className="text-sm text-gray-500">
            {isCallActive 
              ? intl.formatMessage({ id: 'voice.speaking', defaultMessage: 'Parlez maintenant...' })
              : intl.formatMessage({ id: 'voice.description', defaultMessage: 'Prenez rendez-vous par la voix' })
            }
          </p>
        </div>

        {/* Error display */}
        {error && (
          <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {/* Volume indicator */}
        {isCallActive && (
          <div className="mb-4">
            <div className="flex items-center justify-center gap-1">
              {[...Array(10)].map((_, i) => (
                <div
                  key={i}
                  className={`w-1 rounded-full transition-all duration-100 ${
                    i < volumeLevel * 10 ? 'bg-green-500' : 'bg-gray-200'
                  }`}
                  style={{ height: `${12 + i * 2}px` }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3">
          {isCallActive ? (
            <>
              <Button
                variant={isMuted ? "destructive" : "outline"}
                onClick={toggleMute}
                className="flex-1"
              >
                {isMuted ? (
                  <>
                    <MicOff className="w-4 h-4 mr-2" />
                    {intl.formatMessage({ id: 'voice.unmute', defaultMessage: 'Activer micro' })}
                  </>
                ) : (
                  <>
                    <Mic className="w-4 h-4 mr-2" />
                    {intl.formatMessage({ id: 'voice.mute', defaultMessage: 'Couper micro' })}
                  </>
                )}
              </Button>
              <Button
                variant="destructive"
                onClick={endCall}
                className="flex-1"
              >
                <PhoneOff className="w-4 h-4 mr-2" />
                {intl.formatMessage({ id: 'voice.endCall', defaultMessage: 'Raccrocher' })}
              </Button>
            </>
          ) : (
            <Button
              onClick={handleCallClick}
              disabled={isConnecting}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
            >
              {isConnecting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  {intl.formatMessage({ id: 'voice.connecting', defaultMessage: 'Connexion...' })}
                </>
              ) : (
                <>
                  <Phone className="w-4 h-4 mr-2" />
                  {intl.formatMessage({ id: 'voice.callAssistant', defaultMessage: 'Appeler l\'assistant' })}
                </>
              )}
            </Button>
          )}
        </div>

        {/* Transcript */}
        {transcript.length > 0 && (
          <div className="mt-4 max-h-40 overflow-y-auto">
            <p className="text-xs text-gray-400 mb-2">
              {intl.formatMessage({ id: 'voice.transcript', defaultMessage: 'Transcription' })}
            </p>
            {transcript.slice(-5).map((t, i) => (
              <div 
                key={i} 
                className={`text-sm p-2 rounded mb-1 ${
                  t.role === 'user' ? 'bg-blue-50 text-blue-800' : 'bg-gray-50 text-gray-800'
                }`}
              >
                <span className="font-medium">{t.role === 'user' ? 'Vous' : 'Assistant'}: </span>
                {t.text}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default VoiceAssistantButton;
